import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { api, errorMessage } from "@/lib/api";
import { PATIENTS } from "@/constants/testIds";

const empty = { nome: "", cognome: "", data_nascita: null, diagnosi: "", note: "" };

export const PatientDialog = ({ open, onOpenChange, patient, onSaved }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const isEdit = Boolean(patient);

  useEffect(() => {
    if (open) setForm(patient ? { nome: patient.nome || "", cognome: patient.cognome || "", data_nascita: patient.data_nascita || null, diagnosi: patient.diagnosi || "", note: patient.note || "" } : empty);
  }, [open, patient]);

  const mutation = useMutation({
    mutationFn: async () => (isEdit ? (await api.put(`/patients/${patient.patient_id}`, form)).data : (await api.post("/patients", form)).data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["patients"] });
      if (isEdit) qc.invalidateQueries({ queryKey: ["patient", patient.patient_id] });
      toast.success(isEdit ? "Paziente aggiornato" : "Paziente creato");
      onOpenChange(false);
      onSaved?.(data);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card" data-testid={PATIENTS.dialog}>
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Modifica paziente" : "Nuovo paziente"}</DialogTitle>
          <DialogDescription>Inserisci i dati anagrafici essenziali. Potrai completarli in seguito.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!form.nome.trim()) return toast.error("Il nome è obbligatorio");
            mutation.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={set("nome")} placeholder="Luca" className="bg-card" data-testid={PATIENTS.nomeInput} autoFocus />
            </div>
            <div className="space-y-2">
              <Label>Cognome</Label>
              <Input value={form.cognome} onChange={set("cognome")} placeholder="B." className="bg-card" data-testid={PATIENTS.cognomeInput} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Data di nascita</Label>
            <DatePicker value={form.data_nascita} onChange={(v) => setForm((f) => ({ ...f, data_nascita: v }))} testId="patient-birthdate-button" />
          </div>
          <div className="space-y-2">
            <Label>Diagnosi / riferimento clinico</Label>
            <Input value={form.diagnosi} onChange={set("diagnosi")} placeholder="es. Disturbo dello spettro autistico, livello 2" className="bg-card" data-testid={PATIENTS.diagnosiInput} />
          </div>
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea value={form.note} onChange={set("note")} placeholder="Rinforzatori preferiti, indicazioni per la seduta…" className="bg-card min-h-[80px]" data-testid={PATIENTS.noteInput} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={mutation.isPending} data-testid={PATIENTS.saveButton}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? "Salva modifiche" : "Crea paziente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
